import React from "react";
import { Alert, Badge, Button, Card, Table } from "components/ui/BootstrapCompat";
import { WithdrawRequest, WithdrawRequestStatus } from "../withdraw.types";
import {
  formatCurrency,
  formatDateTime,
  getWithdrawNetAmount,
  normalizeWithdrawRequests,
} from "../withdraw.utils";
import { useQuery } from "react-query";
import { donateWithdrawGetMany } from "services/withdraw.service";
import useLocalStorage from "helpers/useLocalStorage";

type Props = {
  currencyLabel?: string;
};

const statusMap: Record<
  WithdrawRequestStatus,
  { label: string; variant: string }
> = {
  PENDING: { label: "ລໍຖ້າດຳເນີນການ", variant: "warning" },
  SUCCESS: { label: "ສຳເລັດ", variant: "success" },
  FAILED: { label: "ບໍ່ສຳເລັດ", variant: "danger" },
  CANCELED: { label: "ຍົກເລີກ", variant: "secondary" },
};

const getErrorMessage = (error: any) =>
  error?.response?.data?.message || error?.message || "ໂຫລດລາຍການຖອນບໍ່ສຳເລັດ";

export default function WithdrawHistoryTable({
  currencyLabel = "ກີບ",
}: Props) {
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");

  const queryKey = React.useMemo(
    () => ["withdraw-get-many", businessId || "streamer"],
    [businessId],
  );
  const {
    data = [],
    error,
    isError,
    isLoading,
    isFetching,
    refetch,
  } = useQuery<WithdrawRequest[]>(
    queryKey,
    async () => {
      if (!token) return [];
      const response = await donateWithdrawGetMany({
        token,
      });

      return normalizeWithdrawRequests(response);
    },
    { enabled: !!token, retry: false },
  );

  return (
    <Card>
      <Card.Header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span>ລາຍການປະຫວັດການຖອນ</span>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? "ກຳລັງໂຫລດ..." : "ຣີເຟຣດ"}
        </Button>
      </Card.Header>
      <Card.Body>
        {isError && (
          <Alert variant="danger" className="mb-3">
            {getErrorMessage(error)}
          </Alert>
        )}

        {isLoading ? (
          <div className="rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
            ກຳລັງໂຫລດລາຍການຖອນ...
          </div>
        ) : data.length ? (
          <div className="overflow-x-auto">
            <Table hover className="mb-0 min-w-[1080px]">
              <thead>
                <tr>
                  <th style={{ width: 170 }}>ວັນເວລາ</th>
                  <th>ເລກທີລາຍການ</th>
                  <th className="text-right">ຍອດຫັກ</th>
                  <th>ຄ່າທຳນຽມ</th>
                  <th>ຍອດສຸດທິ</th>
                  <th>ຍອດກ່ອນຖອນ</th>
                  <th>ຍອດຫຼັງຖອນ</th>
                  <th style={{ width: 140 }}>ສະຖານະ</th>
                  <th>ໝາຍເຫດ</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => {
                  const status = statusMap[item.status] || {
                    label: item.status,
                    variant: "light",
                  };

                  return (
                    <tr key={item.id}>
                      <td className="text-nowrap">
                        {formatDateTime(item.createdAt)}
                      </td>
                      <td>
                        <div className="max-w-[180px] truncate font-mono text-[11px] text-gray-600">
                          {item.id}
                        </div>
                      </td>
                      <td className="text-nowrap text-right font-semibold text-primary">
                        {formatCurrency(item.amount)} {currencyLabel}
                      </td>
                      <td className="text-nowrap text-right">
                        {formatCurrency(item.feeAmount)} {currencyLabel}
                      </td>
                      <td className="text-nowrap text-right">
                        {formatCurrency(getWithdrawNetAmount(item))}{" "}
                        {currencyLabel}
                      </td>
                      <td className="text-nowrap text-right">
                        {formatCurrency(item.balanceBefore)} {currencyLabel}
                      </td>
                      <td className="text-nowrap text-right">
                        {formatCurrency(item.balanceAfter)} {currencyLabel}
                      </td>
                      <td className="text-nowrap">
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </td>
                      <td className="text-truncate" style={{ maxWidth: 260 }}>
                        {item.message || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
            ຍັງບໍ່ມີປະຫວັດການຖອນ
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
